import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { IconInfo } from '../../../utils/icons';
import { useUser } from '../../../hooks/useUser';
import { useProductDetailLink } from '../../../hooks/useProductDetailLink';
import { isInternal } from '../../../utils';
import type {
    ProductIdeal,
    IdealExpert,
    IdealProfile,
} from '../../../hooks/useProjectExpertScores';

interface Product {
    desireid: number;
    title: string;
    category_name?: string | null;
}

interface Props {
    products: Product[];
    idealsByDesireId: Record<string, ProductIdeal>;
    brandId?: number | string | null;
}

const ALGO_EXPLANATION = (
    <span style={{ fontSize: 12, lineHeight: 1.5 }}>
        <strong>How the algorithm scores experts</strong>
        <br />
        SCORE = band(α) + width(α) × (A × Q)
        <br />
        <br />
        <strong>α — category affinity (the primary signal).</strong> Does the expert's work
        touch this product's category? Exact category = 1.0, same family = 0.7, same broad
        area = 0.4, none = 0 — taking the best match across the product's categories.
        <br />
        <br />
        α sorts experts into disjoint <strong>bands</strong>, so anyone who works in the
        right category outranks anyone who doesn't — regardless of how well their attributes
        line up. Within a band, the refinement <strong>A × Q</strong> sets the order:
        <br />
        <strong>A</strong> — weighted Jaccard alignment across the attributes the product targets.
        <br />
        <strong>Q</strong> = Σ(W·E) — <strong>W</strong> normalized profession relevance,{" "}
        <strong>E</strong> experience curve per profession: 1 − e^(−years/5).
        <br />
        <br />
        <strong>Top Experts</strong> = the highest-scoring verified creators we actually
        have for this product. <strong>Top Profile</strong> = the ideal profession combo
        to look for, computed from the taxonomy (may not exist as a real creator yet).
        Because affinity is banded, broadening an expert's category coverage moves them up
        far more than attribute tweaks; pairing complementary professions still lifts A × Q
        within a band.
    </span>
);

function primaryProfessionLabel(expert: IdealExpert) {
    const first = (expert.professions || [])[0];
    if (!first) return '—';
    const sub = first.subProfessionName || '';
    const parent = first.professionName || '';
    if (sub && parent) return `${sub} (${parent})`;
    return sub || parent || '—';
}

function profileLabel(p: IdealProfile) {
    const labels = (p.members || []).map((m) => {
        const sub = m.subProfessionName || '—';
        const parent = m.professionName ? ` (${m.professionName})` : '';
        return `${sub}${parent}`;
    });
    return labels.length > 0 ? labels.join(' + ') : '—';
}

function attrTooltipRows(ideal: ProductIdeal) {
    const attrs = ideal.attributes || [];
    if (attrs.length === 0) return null;
    return (
        <>
            <strong>Ideal attributes</strong>
            <br />
            {attrs.map((g, i) => (
                <React.Fragment key={g.attrSlug}>
                    <span style={{ color: '#C9C9C9' }}>{g.attrName}:</span>{' '}
                    {(g.values || []).join(', ')}
                    {i < attrs.length - 1 ? <br /> : null}
                </React.Fragment>
            ))}
        </>
    );
}

const IdealExpertExplanation: React.FC<Props> = ({ products, idealsByDesireId, brandId }) => {
    const { role } = useUser();
    const { goToProduct } = useProductDetailLink();
    if (!isInternal(role)) return null;

    const productsWithIdeals = products.filter((p) => {
        const ideal = idealsByDesireId[String(p.desireid)];
        if (!ideal) return false;
        const attrs = ideal.attributes || [];
        const experts = ideal.topExperts || [];
        const profiles = ideal.topProfiles || [];
        return attrs.length > 0 || experts.length > 0 || profiles.length > 0;
    });

    if (productsWithIdeals.length === 0) return null;

    return (
        <div className="flex flex-col gap-2 ml-auto min-w-0 max-w-[60%]">
            <div className="flex items-center gap-2 justify-end">
                <span className="text-[14px] font-medium leading-[132%] text-[var(--Base-900,#444)] font-circular">
                    Top experts & ideal profile per product
                </span>
                <Tooltip placement="left" arrow title={ALGO_EXPLANATION}>
                    <span className="inline-flex items-center cursor-help text-text-2">
                        <IconInfo width={14} height={14} />
                    </span>
                </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
                {productsWithIdeals.map((p) => {
                    const ideal = idealsByDesireId[String(p.desireid)];
                    const experts = ideal.topExperts || [];
                    const profiles = ideal.topProfiles || [];
                    const bestExpert = experts[0];
                    const bestProfile = profiles[0];

                    const expPct = bestExpert ? Math.round((bestExpert.score || 0) * 100) : null;
                    // Band colors: ≥75 uses-exact (green), 60–74 family, 40–59 broad, <40 context-only.
                    let expBg = '#F2EEE8';
                    let expFg = '#9B958C';
                    if (expPct !== null) {
                        if (expPct >= 75) { expBg = '#E6F0EA'; expFg = '#5A9E6F'; }
                        else if (expPct >= 60) { expBg = '#EEE7DA'; expFg = '#917A45'; }
                        else if (expPct >= 40) { expBg = '#F4ECDD'; expFg = '#AB854A'; }
                        else { expBg = '#FBE9E2'; expFg = '#D4725C'; }
                    }
                    const profPct = bestProfile ? Math.round((bestProfile.matchScore || 0) * 100) : null;

                    const cardTooltip = (
                        <span style={{ fontSize: 12, lineHeight: 1.5 }}>
                            <strong>{p.title}</strong>
                            <br />
                            <br />
                            <strong>Top experts (real)</strong>
                            <br />
                            {experts.length > 0 ? (
                                experts.map((expert, i) => (
                                    <React.Fragment key={expert.userId}>
                                        {(expert.displayName || `User ${expert.userId}`)} —{' '}
                                        {primaryProfessionLabel(expert)} —{' '}
                                        {Math.round((expert.score || 0) * 100)}%
                                        {i < experts.length - 1 ? <br /> : null}
                                    </React.Fragment>
                                ))
                            ) : (
                                <em>No matched experts yet — run a recompute.</em>
                            )}
                            <br />
                            <br />
                            <strong>Ideal profiles (target)</strong>
                            <br />
                            {profiles.length > 0 ? (
                                profiles.map((profile, i) => (
                                    <React.Fragment key={i}>
                                        {profileLabel(profile)} — {Math.round((profile.matchScore || 0) * 100)}%
                                        {i < profiles.length - 1 ? <br /> : null}
                                    </React.Fragment>
                                ))
                            ) : (
                                <em>No profile match.</em>
                            )}
                            <br />
                            <br />
                            {attrTooltipRows(ideal)}
                        </span>
                    );

                    return (
                        <Tooltip
                            key={p.desireid}
                            placement="bottom-end"
                            arrow
                            title={cardTooltip}
                        >
                            <div
                                className="inline-flex flex-col gap-[3px] rounded-md border border-border-token bg-surface px-[10px] py-[6px] cursor-help"
                                style={{ maxWidth: 320 }}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span
                                        onClick={
                                            brandId != null
                                                ? (e) => {
                                                      e.stopPropagation();
                                                      goToProduct({ desireid: p.desireid, brand_id: brandId });
                                                  }
                                                : undefined
                                        }
                                        className={`text-[11px] font-semibold font-circular text-text-1 truncate${
                                            brandId != null ? " cursor-pointer hover:text-accent-text" : ""
                                        }`}
                                        title={p.title}
                                    >
                                        {p.title}
                                    </span>
                                    {p.category_name && (
                                        <Tooltip title={p.category_name}>
                                            <span className="shrink-0 text-[9px] font-medium font-circular text-[#7B6090] bg-[#F0EBF5] rounded-full px-[6px] py-[1px] truncate max-w-[120px]">
                                                {p.category_name}
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>

                                {/* Top expert (real) */}
                                <div className="flex items-center gap-1">
                                    <span className="text-[9px] uppercase tracking-wide text-text-2 font-circular shrink-0 w-[34px]">
                                        Expert
                                    </span>
                                    <span
                                        className="text-[11px] font-circular text-text-1 truncate"
                                        title={bestExpert ? primaryProfessionLabel(bestExpert) : ''}
                                    >
                                        {bestExpert ? (bestExpert.displayName || `User ${bestExpert.userId}`) : 'None yet'}
                                    </span>
                                    {expPct !== null && (
                                        <span
                                            className="text-[10px] font-bold font-circular leading-none rounded-full px-[5px] py-[2px] ml-auto shrink-0"
                                            style={{ background: expBg, color: expFg }}
                                        >
                                            {expPct}%
                                        </span>
                                    )}
                                </div>

                                {/* Top profile (hypothetical) */}
                                <div className="flex items-center gap-1">
                                    <span className="text-[9px] uppercase tracking-wide text-text-2 font-circular shrink-0 w-[34px]">
                                        Profile
                                    </span>
                                    <span
                                        className="text-[11px] font-circular text-text-2 truncate"
                                        title={bestProfile ? profileLabel(bestProfile) : ''}
                                    >
                                        {bestProfile ? profileLabel(bestProfile) : '—'}
                                    </span>
                                    {profPct !== null && (
                                        <span className="text-[10px] font-bold font-circular leading-none text-[#7B6090] ml-auto shrink-0">
                                            {profPct}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
};

export default IdealExpertExplanation;
